import styled from '@emotion/styled';
import {media, Spacer, spacing} from '@nfq/react-grid';

import {Button} from 'UI/components/action/Button';
import {P} from 'UI/components/layout/Text';

import {useInitialConsent} from './useInitialConsent';

/**
 * The `ComponentProps` interface defines the properties for the `InitialConsent` component.
 * This interface specifies the required and optional props needed to render the cookie consent modal dialog.
 * It includes handlers for user interactions and testing capabilities to ensure proper functionality and testability.
 */
interface ComponentProps {
    /**
     * Function to handle closing the consent modal dialog.
     * This callback is invoked when the user makes a consent decision and can optionally accept a cleanup function to execute after closing.
     * The onClose parameter allows for additional cleanup or state management after the modal is dismissed.
     */
    handleClose(onClose?: () => void): void;
    /**
     * Optional function to handle navigation to detailed cookie settings.
     * This callback is triggered when the user clicks the "Einstellungen" (Settings) button to access more granular cookie preferences.
     * If not provided, the settings button functionality may be limited or hidden.
     */
    handleNext?(): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'InitialConsent'
     */
    testId?: string;
}

/**
 * The InitialConsent component renders a modal dialog for cookie consent management.
 * It displays information about cookie usage and provides three action buttons for users to accept all cookies, reject non-essential cookies, or access detailed settings.
 * This component is typically shown on first visit to handle GDPR compliance requirements.
 *
 * @param props             The component props.
 * @param props.handleClose Function to handle closing the consent modal, optionally accepting a callback function.
 * @param props.handleNext  Optional function to handle navigation to detailed cookie settings.
 * @param props.testId      Test identifier for the component used in automated testing.
 * @returns The rendered InitialConsent component JSX.
 *
 * @example
 * ```tsx
 * <InitialConsent
 *     handleClose={(onClose) => setIsOpen(false)}
 *     handleNext={() => setShowDetails(true)}
 *     testId="cookie-consent"
 * />
 * ```
 */
const InitialConsent = ({handleClose, handleNext, testId = 'InitialConsent'}: ComponentProps) => {
    const {handleAcceptAll, handleReject} = useInitialConsent(handleClose);

    return (
        <Wrapper data-cy={testId}>
            <P>
                Wir verwenden Cookies für die Sicherheit unserer Website und zur anonymen
                Analyse der Websitenutzung. Notwendige Sicherheits-Cookies können nicht deaktiviert werden.
                Weitere Informationen finden Sie in unserer Cookie-Richtlinie.
            </P>
            <Spacer y={8} />
            <ButtonWrapper>
                <Button
                    as="button"
                    size="small"
                    theme="dark"
                    variant="outlined"
                    onClick={handleNext}
                >
                    Einstellungen
                </Button>
                <Button as="button" size="small" variant="secondary" onClick={handleReject}>Nur notwendige</Button>
                <Button as="button" size="small" onClick={handleAcceptAll}>Alle akzeptieren</Button>
            </ButtonWrapper>
        </Wrapper>
    );
};

InitialConsent.displayName = 'InitialConsent';

export {InitialConsent};

const Wrapper = styled.div`
    padding: ${spacing(8)};
`;

const ButtonWrapper = styled.div`
    align-items: stretch;
    display: flex;
    flex-direction: column-reverse;
    gap: ${spacing(4)};
    justify-content: end;

    & > button {
        justify-content: center;
        width: 100%;
    }

    ${media('sm')} {
        align-items: stretch;
        flex-direction: row;

        & > button {
            width: fit-content;
        }
    }
`;